import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useAuthApi } from './useAuthApi';
import { USER_ROLES } from '../config/api';

const AUTH_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { loginApi, registerApi, sendRegisterOtpApi, verifyRegisterOtpApi, forgotPasswordApi, resetPasswordApi } = useAuthApi();

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    const storedExpiresAt = localStorage.getItem('authExpiresAt');
    
    if (storedToken && storedUser) {
      // Check if token is expired
      if (storedExpiresAt && new Date() > new Date(storedExpiresAt)) {
        // Token expired, clear storage
        clearAuthData();
      } else {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }
    }
    setLoading(false);
  }, []);

  // Store auth data in localStorage
  const storeAuthData = (token, user, expiresAt) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(user));
    if (expiresAt) {
      localStorage.setItem('authExpiresAt', expiresAt);
    }
  };

  // Clear auth data from localStorage
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('authExpiresAt');
    setToken(null);
    setUser(null);
  }, []);

  // Login function with role-based redirect
  const login = async (email, password, navigate) => {
    try {
      const result = await loginApi(email, password);
      
      if (result.success) {
        // Do NOT store admin users in regular auth context
        if (result.user.role === USER_ROLES.ADMIN) {
          // Return success but don't store in regular auth
          return {
            success: true,
            token: result.token,
            user: result.user,
            expiresAt: result.expiresAt,
            data: result.data
          };
        }
        
        // Only store customer users in regular auth context
        // Store authentication data
        storeAuthData(result.token, result.user, result.expiresAt);
        
        // Update state
        setToken(result.token);
        setUser(result.user);
        
        // Navigate if provided
        if (navigate) {
          navigate('/');
        }
        
        return {
          success: true,
          token: result.token,
          user: result.user,
          expiresAt: result.expiresAt,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Login failed. Please try again.'
      };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const result = await registerApi(userData);
      
      if (result.success) {
        return {
          success: true,
          userId: result.userId,
          message: result.message,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message,
          errors: result.errors || []
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Registration failed. Please try again.',
        errors: [error.message]
      };
    }
  };

  // Send OTP for registration email verification
  const sendRegisterOtp = async (email) => {
    try {
      const result = await sendRegisterOtpApi(email);
      return result.success
        ? { success: true, message: result.message, data: result.data }
        : { success: false, message: result.message, errors: result.errors || [] };
    } catch (error) {
      return { success: false, message: 'Failed to send OTP. Please try again.', errors: [error.message] };
    }
  };

  // Verify OTP for registration email verification
  const verifyRegisterOtp = async (email, otp) => {
    try {
      const result = await verifyRegisterOtpApi(email, otp);
      return result.success
        ? { success: true, message: result.message, data: result.data }
        : { success: false, message: result.message, errors: result.errors || [] };
    } catch (error) {
      return { success: false, message: 'OTP verification failed. Please try again.', errors: [error.message] };
    }
  };

  // Forgot Password function
  const forgotPassword = async (emailOrMobile, channel = "email") => {
    try {
      const result = await forgotPasswordApi(emailOrMobile, channel);
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message,
          errors: result.errors || []
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Failed to send reset email. Please try again.',
        errors: [error.message]
      };
    }
  };

  // Reset Password function
  const resetPassword = async (emailOrMobile, code, newPassword) => {
    try {
      const result = await resetPasswordApi(emailOrMobile, code, newPassword);
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          data: result.data
        };
      } else {
        return {
          success: false,
          message: result.message,
          errors: result.errors || []
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Password reset failed. Please try again.',
        errors: [error.message]
      };
    }
  };

  // Logout function
  const logout = (navigate) => {
    clearAuthData();
    if (navigate) {
      navigate('/');
    }
  };

  // Auto logout on inactivity
  useEffect(() => {
    if (!token) return;

    let timeoutId;

    const resetTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        clearAuthData();
      }, AUTH_INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetTimer));

    resetTimer();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetTimer));
    };
  }, [token, clearAuthData]);

  // Check if user is authenticated
  const isAuthenticated = () => {
    if (!token || !user) return false;
    
    // Check token expiration
    const expiresAt = localStorage.getItem('authExpiresAt');
    if (expiresAt && new Date() > new Date(expiresAt)) {
      // Token expired, clear data
      clearAuthData();
      return false;
    }
    
    return true;
  };

  // Get current valid token
  const getValidToken = () => {
    if (!isAuthenticated()) return null;
    return token;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.role === USER_ROLES.ADMIN;
  };

  // Check if user is customer
  const isCustomer = () => {
    return user?.role === USER_ROLES.CUSTOMER;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    sendRegisterOtp,
    verifyRegisterOtp,
    forgotPassword,
    resetPassword,
    logout,
    isAuthenticated,
    isAdmin,
    isCustomer,
    getValidToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
